import { useEffect, useState } from "react";
import { Plus, Pencil, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useLang } from "../i18n";

const empty = { name: "", address: "", gstin: "", phone: "", email: "", payment_terms: "", dl_no: "" };

export default function Companies() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const { t } = useLang();

  const load = async () => setRows((await api.get("/companies", { params: { q } })).data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/companies/${editId}`, form);
      else await api.post("/companies", form);
      toast.success(editId ? "Company updated" : "Company added");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div data-testid="companies-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("companies")}</h1>
          <p className="sgp-page-subtitle">Ek baar add karo — har purchase bill mein dropdown se select hoga.</p>
        </div>
        <button data-testid="add-company-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> {t("add")}
        </button>
      </div>

      <input data-testid="companies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company by name..." className="sgp-input max-w-sm" />

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr>
              <th className="sgp-th">Company</th><th className="sgp-th">GSTIN</th><th className="sgp-th">DL No.</th>
              <th className="sgp-th">Contact</th><th className="sgp-th">Payment Terms</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="companies-table">
            {rows.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} className="sgp-table-row">
                <td className="sgp-td">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-md bg-teal-50 border border-teal-100 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-teal-700" />
                    </div>
                    <div><p className="font-semibold text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.address}</p></div>
                  </div>
                </td>
                <td className="sgp-td font-mono text-xs">{c.gstin || "-"}</td>
                <td className="sgp-td font-mono text-xs">{c.dl_no || "-"}</td>
                <td className="sgp-td text-xs">{c.phone}<br /><span className="text-slate-400">{c.email}</span></td>
                <td className="sgp-td text-xs">{c.payment_terms || "-"}</td>
                <td className="sgp-td">
                  <button data-testid={`edit-company-${c.id}`} onClick={() => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); }}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700"><Pencil className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="sgp-td text-center py-10 text-slate-400">Koi company nahi mili. Pehle supplier add karo.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="company-form-modal" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Company" : "Add Company (Supplier)"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><label className="sgp-label">Company Name *</label>
              <input data-testid="company-name-input" required className="sgp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Address</label>
              <input data-testid="company-address-input" className="sgp-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><label className="sgp-label">GSTIN</label>
              <input data-testid="company-gstin-input" className="sgp-input font-mono" maxLength={15} value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} /></div>
            <div><label className="sgp-label">Drug License No.</label>
              <input data-testid="company-dl-input" className="sgp-input font-mono" value={form.dl_no} onChange={(e) => setForm({ ...form, dl_no: e.target.value })} /></div>
            <div><label className="sgp-label">Phone</label>
              <input data-testid="company-phone-input" className="sgp-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="sgp-label">Email</label>
              <input data-testid="company-email-input" type="email" className="sgp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2"><label className="sgp-label">Payment Terms (e.g. 30 din mein payment)</label>
              <input data-testid="company-terms-input" className="sgp-input" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></div>
            <div className="col-span-2 flex justify-end gap-2 mt-1">
              <button type="button" data-testid="company-cancel-btn" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button type="submit" data-testid="company-save-btn" className="sgp-primary-btn">{t("save")}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
